import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export const INPUTNUMBER_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => InputNumberComponent),
  multi: true,
};
@Component({
  selector: 'sigv-input-number',
  template: `
    <span
      [ngClass]="{
        'p-inputnumber p-component': true,
        'p-inputnumber-buttons-stacked':
          this.showButtons && this.buttonLayout === 'stacked',
        'p-inputnumber-buttons-horizontal':
          this.showButtons && this.buttonLayout === 'horizontal',
        'p-inputnumber-buttons-vertical':
          this.showButtons && this.buttonLayout === 'vertical'
      }"
      [ngStyle]="style"
      [class]="styleClass"
    >
      <input
        #input
        [ngClass]="'p-inputnumber-input'"
        [ngStyle]="inputStyle"
        [class]="inputStyleClass"
        pInputText
        [value]="formattedValue()"
        [attr.placeholder]="placeholder"
        [attr.title]="title"
        [attr.id]="inputId"
        [attr.size]="size"
        [attr.name]="name"
        [attr.autocomplete]="autocomplete"
        [attr.maxlength]="maxlength"
        [attr.tabindex]="tabindex"
        [attr.aria-label]="ariaLabel"
        [attr.aria-required]="ariaRequired"
        [disabled]="disabled"
        [attr.required]="required"
        [attr.min]="min"
        [attr.max]="max"
        [readonly]="readonly"
        inputmode="decimal"
        (input)="onUserInput($event)"
        (keydown)="onInputKeyDown($event)"
        (keypress)="onInputKeyPress($event)"
        (paste)="onPaste($event)"
        (click)="onInputClick()"
        (focus)="onInputFocus($event)"
        (blur)="onInputBlur($event)"
      />
      <i
        *ngIf="buttonLayout != 'vertical' && showClear && value"
        class="p-inputnumber-clear-icon pi pi-times"
        (click)="clear()"
      ></i>
      <span
        class="p-inputnumber-button-group"
        *ngIf="showButtons && buttonLayout === 'stacked'"
      >
        <button
          type="button"
          pButton
          [ngClass]="{ 'p-inputnumber-button p-inputnumber-button-up': true }"
          [class]="incrementButtonClass"
          [icon]="incrementButtonIcon"
          [disabled]="disabled"
          (mousedown)="this.onUpButtonMouseDown($event)"
          (mouseup)="onUpButtonMouseUp()"
          (mouseleave)="onUpButtonMouseLeave()"
          (keydown)="onUpButtonKeyDown($event)"
          (keyup)="onUpButtonKeyUp()"
        ></button>
        <button
          type="button"
          pButton
          [ngClass]="{ 'p-inputnumber-button p-inputnumber-button-down': true }"
          [class]="decrementButtonClass"
          [icon]="decrementButtonIcon"
          [disabled]="disabled"
          (mousedown)="this.onDownButtonMouseDown($event)"
          (mouseup)="onDownButtonMouseUp()"
          (mouseleave)="onDownButtonMouseLeave()"
          (keydown)="onDownButtonKeyDown($event)"
          (keyup)="onDownButtonKeyUp()"
        ></button>
      </span>
      <button
        type="button"
        pButton
        [ngClass]="{ 'p-inputnumber-button p-inputnumber-button-up': true }"
        [class]="incrementButtonClass"
        [icon]="incrementButtonIcon"
        *ngIf="showButtons && buttonLayout !== 'stacked'"
        [disabled]="disabled"
        (mousedown)="this.onUpButtonMouseDown($event)"
        (mouseup)="onUpButtonMouseUp()"
        (mouseleave)="onUpButtonMouseLeave()"
        (keydown)="onUpButtonKeyDown($event)"
        (keyup)="onUpButtonKeyUp()"
      ></button>
      <button
        type="button"
        pButton
        [ngClass]="{ 'p-inputnumber-button p-inputnumber-button-down': true }"
        [class]="decrementButtonClass"
        [icon]="decrementButtonIcon"
        *ngIf="showButtons && buttonLayout !== 'stacked'"
        [disabled]="disabled"
        (mousedown)="this.onDownButtonMouseDown($event)"
        (mouseup)="onDownButtonMouseUp()"
        (mouseleave)="onDownButtonMouseLeave()"
        (keydown)="onDownButtonKeyDown($event)"
        (keyup)="onDownButtonKeyUp()"
      ></button>
    </span>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [INPUTNUMBER_VALUE_ACCESSOR],
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./input-number.component.css'],
  // tslint:disable-next-line: no-host-metadata-property
  host: {
    class: 'p-element p-inputwrapper',
    '[class.p-inputwrapper-filled]': 'filled',
    '[class.p-inputwrapper-focus]': 'focused',
    '[class.p-inputnumber-clearable]':
      'showClear && buttonLayout != "vertical"',
  },
})
export class InputNumberComponent
  implements OnInit, OnChanges, ControlValueAccessor
{
  @Input() get disabled(): boolean {
    return this._disabled;
  }

  set disabled(disabled: boolean) {
    if (disabled) {
      this.focused = false;
    }

    this._disabled = disabled;

    if (this.timer) {
      this.clearTimer();
    }
  }

  constructor(public el: ElementRef, private cd: ChangeDetectorRef) {}

  get filled(): boolean {
    return this.value != null && this.value.toString().length > 0;
  }

  @Input() showButtons = false;

  @Input() format = true;

  @Input() buttonLayout = 'stacked';

  @Input() inputId!: string;

  @Input() styleClass!: string;

  @Input() style: any;

  @Input() placeholder!: string;

  @Input() size!: number;

  @Input() maxlength!: number;

  @Input() tabindex!: string;

  @Input() title = '';

  @Input() ariaLabel!: string;

  @Input() ariaRequired!: boolean;

  @Input() name!: string;

  @Input() required!: boolean;

  @Input() autocomplete!: string;

  @Input() min!: number;

  @Input() max!: number;

  @Input() incrementButtonClass!: string;

  @Input() decrementButtonClass!: string;

  @Input() incrementButtonIcon = 'pi pi-angle-up';

  @Input() decrementButtonIcon = 'pi pi-angle-down';

  @Input() readonly = false;

  @Input() step = 1;

  @Input() allowEmpty = true;

  @Input() locale!: string;

  @Input() localeMatcher!: string;

  @Input() mode = 'decimal';

  @Input() currency!: string;

  @Input() currencyDisplay!: string;

  @Input() useGrouping = true;

  @Input() minFractionDigits!: number;

  @Input() maxFractionDigits!: number;

  @Input() prefix!: string;

  @Input() suffix!: string;

  @Input() inputStyle: any;

  @Input() inputStyleClass!: string;

  @Input() showClear = false;

  /** Input 值改變時，model 的值是否及時同步，false 時 onBlur 才同步 */
  @Input() syncModelValue = false;

  @ViewChild('input') input!: ElementRef;

  // tslint:disable-next-line: no-output-on-prefix
  @Output() onInput: EventEmitter<any> = new EventEmitter();

  // tslint:disable-next-line: no-output-on-prefix
  @Output() onFocus: EventEmitter<any> = new EventEmitter();

  // tslint:disable-next-line: no-output-on-prefix
  @Output() onBlur: EventEmitter<any> = new EventEmitter();

  // tslint:disable-next-line: no-output-on-prefix
  @Output() onKeyDown: EventEmitter<any> = new EventEmitter();

  // tslint:disable-next-line: no-output-on-prefix
  @Output() onClear: EventEmitter<any> = new EventEmitter();

  value!: number | null;

  focused!: boolean;

  initialized!: boolean;

  groupChar = '';

  prefixChar = '';

  suffixChar = '';

  isSpecialChar!: boolean;

  timer: any;

  lastValue!: string;

  // tslint:disable-next-line: variable-name
  _numeral: any;

  numberFormat: any;

  // tslint:disable-next-line: variable-name
  _decimal: any;

  // tslint:disable-next-line: variable-name
  _group: any;

  // tslint:disable-next-line: variable-name
  _minusSign: any;

  // tslint:disable-next-line: variable-name
  _currency: any;

  // tslint:disable-next-line: variable-name
  _prefix: any;

  // tslint:disable-next-line: variable-name
  _suffix: any;

  // tslint:disable-next-line: variable-name
  _index: any;

  // tslint:disable-next-line: variable-name
  _disabled!: boolean;

  // tslint:disable-next-line: ban-types
  onModelChange: Function = () => {};

  // tslint:disable-next-line: ban-types
  onModelTouched: Function = () => {};

  ngOnChanges(simpleChange: SimpleChanges): void {
    const props = [
      'locale',
      'localeMatcher',
      'mode',
      'currency',
      'currencyDisplay',
      'useGrouping',
      'minFractionDigits',
      'maxFractionDigits',
      'prefix',
      'suffix',
    ];
    if (props.some((p) => !!simpleChange[p])) {
      this.updateConstructParser();
    }
  }

  ngOnInit(): void {
    this.constructParser();

    this.initialized = true;
  }

  // tslint:disable-next-line: typedef
  getOptions() {
    return {
      localeMatcher: this.localeMatcher,
      style: this.mode,
      currency: this.currency,
      currencyDisplay: this.currencyDisplay,
      useGrouping: this.useGrouping,
      minimumFractionDigits: this.minFractionDigits,
      maximumFractionDigits: this.maxFractionDigits,
    };
  }

  constructParser(): void {
    this.numberFormat = new Intl.NumberFormat(this.locale, this.getOptions());
    const numerals = [
      ...new Intl.NumberFormat(this.locale, { useGrouping: false }).format(
        9876543210
      ),
    ].reverse();
    const index = new Map(numerals.map((d, i) => [d, i]));
    this._numeral = new RegExp(`[${numerals.join('')}]`, 'g');
    this._group = this.getGroupingExpression();
    this._minusSign = this.getMinusSignExpression();
    this._currency = this.getCurrencyExpression();
    this._decimal = this.getDecimalExpression();
    this._suffix = this.getSuffixExpression();
    this._prefix = this.getPrefixExpression();
    this._index = (d: string) => index.get(d);
  }

  updateConstructParser(): void {
    if (this.initialized) {
      this.constructParser();
    }
  }

  escapeRegExp(text: string): string {
    return text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
  }

  getDecimalExpression(): RegExp {
    const formatter = new Intl.NumberFormat(this.locale, {
      ...this.getOptions(),
      useGrouping: false,
    });
    return new RegExp(
      `[${formatter
        .format(1.1)
        .replace(this._currency, '')
        .trim()
        .replace(this._numeral, '')}]`,
      'g'
    );
  }

  getGroupingExpression(): RegExp {
    const formatter = new Intl.NumberFormat(this.locale, { useGrouping: true });
    this.groupChar = formatter
      .format(1000000)
      .trim()
      .replace(this._numeral, '')
      .charAt(0);
    return new RegExp(`[${this.groupChar}]`, 'g');
  }

  getMinusSignExpression(): RegExp {
    const formatter = new Intl.NumberFormat(this.locale, {
      useGrouping: false,
    });
    return new RegExp(
      `[${formatter.format(-1).trim().replace(this._numeral, '')}]`,
      'g'
    );
  }

  getCurrencyExpression(): RegExp {
    if (this.currency) {
      const formatter = new Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency: this.currency,
        currencyDisplay: this.currencyDisplay,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
      return new RegExp(
        `[${formatter
          .format(1)
          .replace(/\s/g, '')
          .replace(this._numeral, '')
          .replace(this._group, '')}]`,
        'g'
      );
    }

    return new RegExp(`[]`, 'g');
  }

  getPrefixExpression(): RegExp {
    if (this.prefix) {
      this.prefixChar = this.prefix;
    } else {
      const formatter = new Intl.NumberFormat(this.locale, {
        style: this.mode,
        currency: this.currency,
        currencyDisplay: this.currencyDisplay,
      });
      this.prefixChar = formatter.format(1).split('1')[0];
    }

    return new RegExp(`${this.escapeRegExp(this.prefixChar || '')}`, 'g');
  }

  getSuffixExpression(): RegExp {
    if (this.suffix) {
      this.suffixChar = this.suffix;
    } else {
      const formatter = new Intl.NumberFormat(this.locale, {
        style: this.mode,
        currency: this.currency,
        currencyDisplay: this.currencyDisplay,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
      this.suffixChar = formatter.format(1).split('1')[1];
    }

    return new RegExp(`${this.escapeRegExp(this.suffixChar || '')}`, 'g');
  }

  formatValue(value?: any): string {
    if (value != null) {
      if (value === '-') {
        // Minus sign
        return value;
      }

      if (this.format) {
        const formatter = new Intl.NumberFormat(this.locale, this.getOptions());
        let formattedValue = formatter.format(value);
        if (this.prefix) {
          formattedValue = this.prefix + formattedValue;
        }

        if (this.suffix) {
          formattedValue = formattedValue + this.suffix;
        }

        return formattedValue;
      }

      return value.toString();
    }

    return '';
  }

  parseValue(text: string): any {
    const filteredText = text
      .replace(this._suffix, '')
      .replace(this._prefix, '')
      .trim()
      .replace(/\s/g, '')
      .replace(this._currency, '')
      .replace(this._group, '')
      .replace(this._minusSign, '-')
      .replace(this._decimal, '.')
      .replace(this._numeral, this._index);

    if (filteredText) {
      if (filteredText === '-') {
        // Minus sign
        return filteredText;
      }

      const parsedValue = +filteredText;
      return isNaN(parsedValue) ? null : parsedValue;
    }

    return null;
  }

  repeat(event: any, interval: any, dir: any): void {
    if (this.readonly) {
      return;
    }

    const i = interval || 500;

    this.clearTimer();
    this.timer = setTimeout(() => {
      this.repeat(event, 40, dir);
    }, i);

    this.spin(event, dir);
  }

  spin(event: any, dir: any): void {
    const step = this.step * dir;
    const currentValue = this.parseValue(this.input.nativeElement.value) || 0;
    const newValue = this.validateValue(currentValue + step);
    if (this.maxlength && this.maxlength < this.formatValue(newValue).length) {
      return;
    }

    this.updateInput(newValue, null, 'spin', null);
    this.updateModel(event, newValue);

    this.handleOnInput(event, currentValue, newValue);
  }

  clear(): void {
    this.value = null;
    this.onModelChange(this.value);
    this.onClear.emit();
  }

  onUpButtonMouseDown(event: any): void {
    this.input.nativeElement.focus();
    this.repeat(event, null, 1);
    event.preventDefault();
  }

  onUpButtonMouseUp(): void {
    this.clearTimer();
  }

  onUpButtonMouseLeave(): void {
    this.clearTimer();
  }

  onUpButtonKeyDown(event: any): void {
    if (event.keyCode === 32 || event.keyCode === 13) {
      this.repeat(event, null, 1);
    }
  }

  onUpButtonKeyUp(): void {
    this.clearTimer();
  }

  onDownButtonMouseDown(event: any): void {
    this.input.nativeElement.focus();
    this.repeat(event, null, -1);
    event.preventDefault();
  }

  onDownButtonMouseUp(): void {
    this.clearTimer();
  }

  onDownButtonMouseLeave(): void {
    this.clearTimer();
  }

  onDownButtonKeyUp(): void {
    this.clearTimer();
  }

  onDownButtonKeyDown(event: any): void {
    if (event.keyCode === 32 || event.keyCode === 13) {
      this.repeat(event, null, -1);
    }
  }

  onUserInput(event: any): void {
    const newValueStr = this.validateValue(this.parseValue(event.target.value));
    const formatValue = this.formatValue(newValueStr);

    if (this.readonly) {
      return;
    }

    if (
      this.isSpecialChar ||
      (this.maxlength && this.maxlength < formatValue.length)
    ) {
      event.target.value = this.lastValue;
    }
    this.isSpecialChar = false;
  }

  onInputKeyDown(event: any): void {
    if (this.readonly) {
      return;
    }

    this.lastValue = event.target.value;
    if (event.shiftKey || event.altKey) {
      this.isSpecialChar = true;
      return;
    }

    const selectionStart = event.target.selectionStart;
    const selectionEnd = event.target.selectionEnd;
    const inputValue = event.target.value;
    let newValueStr = null;

    if (event.altKey) {
      event.preventDefault();
    }

    switch (event.which) {
      // up
      case 38:
        this.spin(event, 1);
        event.preventDefault();
        break;

      // down
      case 40:
        this.spin(event, -1);
        event.preventDefault();
        break;

      // left
      case 37:
        if (!this.isNumeralChar(inputValue.charAt(selectionStart - 1))) {
          event.preventDefault();
        }
        break;

      // right
      case 39:
        if (!this.isNumeralChar(inputValue.charAt(selectionStart))) {
          event.preventDefault();
        }
        break;

      // enter
      case 13:
        newValueStr = this.validateValue(
          this.parseValue(this.input.nativeElement.value)
        );
        this.input.nativeElement.value = this.formatValue(newValueStr);
        this.input.nativeElement.setAttribute('aria-valuenow', newValueStr);
        this.updateModel(event, newValueStr);
        break;

      // backspace
      case 8: {
        event.preventDefault();

        if (selectionStart === selectionEnd) {
          const deleteChar = inputValue.charAt(selectionStart - 1);
          const { decimalCharIndex, decimalCharIndexWithoutPrefix } =
            this.getDecimalCharIndexes(inputValue);

          if (this.isNumeralChar(deleteChar)) {
            const decimalLength = this.getDecimalLength(inputValue);

            if (this._group.test(deleteChar)) {
              this._group.lastIndex = 0;
              newValueStr =
                inputValue.slice(0, selectionStart - 2) +
                inputValue.slice(selectionStart - 1);
            } else if (this._decimal.test(deleteChar)) {
              this._decimal.lastIndex = 0;

              if (decimalLength) {
                this.input.nativeElement.setSelectionRange(
                  selectionStart - 1,
                  selectionStart - 1
                );
              } else {
                newValueStr =
                  inputValue.slice(0, selectionStart - 1) +
                  inputValue.slice(selectionStart);
              }
            } else if (
              decimalCharIndex > 0 &&
              selectionStart > decimalCharIndex
            ) {
              const insertedText =
                this.isDecimalMode() &&
                (this.minFractionDigits || 0) < decimalLength
                  ? ''
                  : '0';
              newValueStr =
                inputValue.slice(0, selectionStart - 1) +
                insertedText +
                inputValue.slice(selectionStart);
            } else if (decimalCharIndexWithoutPrefix === 1) {
              newValueStr =
                inputValue.slice(0, selectionStart - 1) +
                '0' +
                inputValue.slice(selectionStart);
              newValueStr = this.parseValue(newValueStr) > 0 ? newValueStr : '';
            } else {
              newValueStr =
                inputValue.slice(0, selectionStart - 1) +
                inputValue.slice(selectionStart);
            }
          }

          this.updateValue(event, newValueStr, null, 'delete-single');
        } else {
          newValueStr = this.deleteRange(
            inputValue,
            selectionStart,
            selectionEnd
          );
          this.updateValue(event, newValueStr, null, 'delete-range');
        }

        break;
      }

      // del
      case 46:
        event.preventDefault();

        if (selectionStart === selectionEnd) {
          const deleteChar = inputValue.charAt(selectionStart);
          const { decimalCharIndex, decimalCharIndexWithoutPrefix } =
            this.getDecimalCharIndexes(inputValue);

          if (this.isNumeralChar(deleteChar)) {
            const decimalLength = this.getDecimalLength(inputValue);

            if (this._group.test(deleteChar)) {
              this._group.lastIndex = 0;
              newValueStr =
                inputValue.slice(0, selectionStart) +
                inputValue.slice(selectionStart + 2);
            } else if (this._decimal.test(deleteChar)) {
              this._decimal.lastIndex = 0;

              if (decimalLength) {
                this.input.nativeElement.setSelectionRange(
                  selectionStart + 1,
                  selectionStart + 1
                );
              } else {
                newValueStr =
                  inputValue.slice(0, selectionStart) +
                  inputValue.slice(selectionStart + 1);
              }
            } else if (
              decimalCharIndex > 0 &&
              selectionStart > decimalCharIndex
            ) {
              const insertedText =
                this.isDecimalMode() &&
                (this.minFractionDigits || 0) < decimalLength
                  ? ''
                  : '0';
              newValueStr =
                inputValue.slice(0, selectionStart) +
                insertedText +
                inputValue.slice(selectionStart + 1);
            } else if (decimalCharIndexWithoutPrefix === 1) {
              newValueStr =
                inputValue.slice(0, selectionStart) +
                '0' +
                inputValue.slice(selectionStart + 1);
              newValueStr = this.parseValue(newValueStr) > 0 ? newValueStr : '';
            } else {
              newValueStr =
                inputValue.slice(0, selectionStart) +
                inputValue.slice(selectionStart + 1);
            }
          }

          this.updateValue(event, newValueStr, null, 'delete-back-single');
        } else {
          newValueStr = this.deleteRange(
            inputValue,
            selectionStart,
            selectionEnd
          );
          this.updateValue(event, newValueStr, null, 'delete-range');
        }
        break;

      default:
        break;
    }

    this.onKeyDown.emit(event);
  }

  onInputKeyPress(event: any): void {
    if (this.readonly) {
      return;
    }

    event.preventDefault();
    const code = event.which || event.keyCode;
    const char = String.fromCharCode(code);
    const isDecimalSign = this.isDecimalSign(char);
    const isMinusSign = this.isMinusSign(char);

    if ((48 <= code && code <= 57) || isMinusSign || isDecimalSign) {
      this.insert(event, char, { isDecimalSign, isMinusSign });
    }
  }

  onPaste(event: any): void {
    if (!this.disabled && !this.readonly) {
      event.preventDefault();
      const data = (
        event.clipboardData || (window as any).clipboardData
      ).getData('Text');
      if (data) {
        const filteredData = this.parseValue(data);
        if (filteredData != null) {
          this.insert(event, filteredData.toString());
        }
      }
    }
  }

  allowMinusSign(): boolean {
    return this.min == null || this.min < 0;
  }

  isMinusSign(char: string): boolean {
    if (this._minusSign.test(char) || char === '-') {
      this._minusSign.lastIndex = 0;
      return true;
    }

    return false;
  }

  isDecimalSign(char: string): boolean {
    if (this._decimal.test(char)) {
      this._decimal.lastIndex = 0;
      return true;
    }

    return false;
  }

  isDecimalMode(): boolean {
    return this.mode === 'decimal';
  }

  getDecimalCharIndexes(val: any): any {
    const decimalCharIndex = val.search(this._decimal);
    this._decimal.lastIndex = 0;

    const filteredVal = val
      .replace(this._prefix, '')
      .trim()
      .replace(/\s/g, '')
      .replace(this._currency, '');
    const decimalCharIndexWithoutPrefix = filteredVal.search(this._decimal);
    this._decimal.lastIndex = 0;

    return { decimalCharIndex, decimalCharIndexWithoutPrefix };
  }

  getCharIndexes(val: any): any {
    const decimalCharIndex = val.search(this._decimal);
    this._decimal.lastIndex = 0;
    const minusCharIndex = val.search(this._minusSign);
    this._minusSign.lastIndex = 0;
    const suffixCharIndex = val.search(this._suffix);
    this._suffix.lastIndex = 0;
    const currencyCharIndex = val.search(this._currency);
    this._currency.lastIndex = 0;

    return {
      decimalCharIndex,
      minusCharIndex,
      suffixCharIndex,
      currencyCharIndex,
    };
  }

  insert(
    event: any,
    text: any,
    sign = { isDecimalSign: false, isMinusSign: false }
  ): void {
    const minusCharIndexOnText = text.search(this._minusSign);
    this._minusSign.lastIndex = 0;
    if (!this.allowMinusSign() && minusCharIndexOnText !== -1) {
      return;
    }

    const selectionStart = this.input.nativeElement.selectionStart;
    const selectionEnd = this.input.nativeElement.selectionEnd;
    const inputValue = this.input.nativeElement.value.trim();
    const {
      decimalCharIndex,
      minusCharIndex,
      suffixCharIndex,
      currencyCharIndex,
    } = this.getCharIndexes(inputValue);
    let newValueStr;

    if (sign.isMinusSign) {
      if (selectionStart === 0) {
        newValueStr = inputValue;
        if (minusCharIndex === -1 || selectionEnd !== 0) {
          newValueStr = this.insertText(inputValue, text, 0, selectionEnd);
        }

        this.updateValue(event, newValueStr, text, 'insert');
      }
    } else if (sign.isDecimalSign) {
      if (decimalCharIndex > 0 && selectionStart === decimalCharIndex) {
        this.updateValue(event, inputValue, text, 'insert');
      } else if (
        decimalCharIndex > selectionStart &&
        decimalCharIndex < selectionEnd
      ) {
        newValueStr = this.insertText(
          inputValue,
          text,
          selectionStart,
          selectionEnd
        );
        this.updateValue(event, newValueStr, text, 'insert');
      } else if (decimalCharIndex === -1 && this.maxFractionDigits) {
        newValueStr = this.insertText(
          inputValue,
          text,
          selectionStart,
          selectionEnd
        );
        this.updateValue(event, newValueStr, text, 'insert');
      }
    } else {
      const maxFractionDigits =
        this.numberFormat.resolvedOptions().maximumFractionDigits;
      const operation =
        selectionStart !== selectionEnd ? 'range-insert' : 'insert';

      if (decimalCharIndex > 0 && selectionStart > decimalCharIndex) {
        if (
          selectionStart + text.length - (decimalCharIndex + 1) <=
          maxFractionDigits
        ) {
          const charIndex =
            currencyCharIndex >= selectionStart
              ? currencyCharIndex - 1
              : suffixCharIndex >= selectionStart
              ? suffixCharIndex
              : inputValue.length;

          newValueStr =
            inputValue.slice(0, selectionStart) +
            text +
            inputValue.slice(selectionStart + text.length, charIndex) +
            inputValue.slice(charIndex);
          this.updateValue(event, newValueStr, text, operation);
        }
      } else {
        newValueStr = this.insertText(
          inputValue,
          text,
          selectionStart,
          selectionEnd
        );
        this.updateValue(event, newValueStr, text, operation);
      }
    }
  }

  insertText(value: any, text: string, start: number, end: number): string {
    const textSplit = text === '.' ? text : text.split('.');

    if (textSplit.length === 2) {
      const decimalCharIndex = value.slice(start, end).search(this._decimal);
      this._decimal.lastIndex = 0;
      return decimalCharIndex > 0
        ? value.slice(0, start) + this.formatValue(text) + value.slice(end)
        : value || this.formatValue(text);
    } else if (end - start === value.length) {
      return this.formatValue(text);
    } else if (start === 0) {
      return text + value.slice(end);
    } else if (end === value.length) {
      return value.slice(0, start) + text;
    } else {
      return value.slice(0, start) + text + value.slice(end);
    }
  }

  deleteRange(value: any, start: number, end: number): string {
    let newValueStr;

    if (end - start === value.length) {
      newValueStr = '';
    } else if (start === 0) {
      newValueStr = value.slice(end);
    } else if (end === value.length) {
      newValueStr = value.slice(0, start);
    } else {
      newValueStr = value.slice(0, start) + value.slice(end);
    }

    return newValueStr;
  }

  initCursor(): number {
    let selectionStart = this.input.nativeElement.selectionStart;
    let inputValue = this.input.nativeElement.value;
    const valueLength = inputValue.length;
    let index = null;

    // remove prefix
    const prefixLength = (this.prefixChar || '').length;
    inputValue = inputValue.replace(this._prefix, '');
    selectionStart = selectionStart - prefixLength;

    let char = inputValue.charAt(selectionStart);
    if (this.isNumeralChar(char)) {
      return selectionStart + prefixLength;
    }

    // left
    let i = selectionStart - 1;
    while (i >= 0) {
      char = inputValue.charAt(i);
      if (this.isNumeralChar(char)) {
        index = i + prefixLength;
        break;
      } else {
        i--;
      }
    }

    if (index !== null) {
      this.input.nativeElement.setSelectionRange(index + 1, index + 1);
    } else {
      i = selectionStart;
      while (i < valueLength) {
        char = inputValue.charAt(i);
        if (this.isNumeralChar(char)) {
          index = i + prefixLength;
          break;
        } else {
          i++;
        }
      }

      if (index !== null) {
        this.input.nativeElement.setSelectionRange(index, index);
      }
    }

    return index || 0;
  }

  onInputClick(): void {
    if (!this.readonly) {
      this.initCursor();
    }
  }

  isNumeralChar(char: string): boolean {
    if (
      char.length === 1 &&
      (this._numeral.test(char) ||
        this._decimal.test(char) ||
        this._group.test(char) ||
        this._minusSign.test(char))
    ) {
      this.resetRegex();
      return true;
    }

    return false;
  }

  resetRegex(): void {
    this._numeral.lastIndex = 0;
    this._decimal.lastIndex = 0;
    this._group.lastIndex = 0;
    this._minusSign.lastIndex = 0;
  }

  updateValue(
    event: any,
    valueStr: string,
    insertedValueStr: any,
    operation: any
  ): void {
    const currentValue = this.input.nativeElement.value;
    let newValue = null;

    if (valueStr != null) {
      newValue = this.parseValue(valueStr);
      newValue = !newValue && !this.allowEmpty ? 0 : newValue;
      this.updateInput(newValue, insertedValueStr, operation, valueStr);

      if (this.syncModelValue) {
        this.updateModel(event, newValue);
      }

      this.handleOnInput(event, currentValue, newValue);
    }
  }

  handleOnInput(event: any, currentValue: any, newValue: any): void {
    if (this.isValueChanged(currentValue, newValue)) {
      this.onInput.emit({ originalEvent: event, value: newValue });
    }
  }

  isValueChanged(currentValue: any, newValue: any): boolean {
    if (newValue === null && currentValue !== null) {
      return true;
    }

    if (newValue != null) {
      const parsedCurrentValue =
        typeof currentValue === 'string'
          ? this.parseValue(currentValue)
          : currentValue;
      return newValue !== parsedCurrentValue;
    }

    return false;
  }

  validateValue(value: any): any {
    if (value === '-' || value == null) {
      return null;
    }

    if (this.min != null && value < this.min) {
      return this.min;
    }

    if (this.max != null && value > this.max) {
      return this.max;
    }

    return value;
  }

  updateInput(
    value: any,
    insertedValueStr: any,
    operation: any,
    valueStr: any
  ): void {
    insertedValueStr = insertedValueStr || '';

    const valueLength = isNaN(value) ? 0 : Math.floor(value).toString().length;
    const inputValue = this.input.nativeElement.value;
    let newValue = this.formatValue(value);
    const currentLength = inputValue.length;

    if (newValue !== valueStr) {
      newValue = this.concatValues(newValue, valueStr);
    }

    if (currentLength === 0) {
      this.input.nativeElement.value = newValue;
      this.input.nativeElement.setSelectionRange(0, 0);
      const index = this.initCursor();
      const selectionEnd = index + insertedValueStr.length;
      this.input.nativeElement.setSelectionRange(selectionEnd, selectionEnd);
    } else {
      const selectionStart = this.input.nativeElement.selectionStart;
      let selectionEnd = this.input.nativeElement.selectionEnd;
      if (this.maxlength && this.maxlength < valueLength) {
        return;
      }

      this.input.nativeElement.value = newValue;
      const newLength = newValue.length;

      if (operation === 'range-insert') {
        const startValue = this.parseValue(
          (inputValue || '').slice(0, selectionStart)
        );
        const startValueStr = startValue !== null ? startValue.toString() : '';
        const startExpr = startValueStr.split('').join(`(${this.groupChar})?`);
        const sRegex = new RegExp(startExpr, 'g');
        sRegex.test(newValue);

        const tExpr = insertedValueStr.split('').join(`(${this.groupChar})?`);
        const tRegex = new RegExp(tExpr, 'g');
        tRegex.test(newValue.slice(sRegex.lastIndex));

        selectionEnd = sRegex.lastIndex + tRegex.lastIndex;
        this.input.nativeElement.setSelectionRange(selectionEnd, selectionEnd);
      } else if (newLength === currentLength) {
        if (operation === 'insert' || operation === 'delete-back-single') {
          this.input.nativeElement.setSelectionRange(
            selectionEnd + 1,
            selectionEnd + 1
          );
        } else if (operation === 'delete-single') {
          this.input.nativeElement.setSelectionRange(
            selectionEnd - 1,
            selectionEnd - 1
          );
        } else if (operation === 'delete-range' || operation === 'spin') {
          this.input.nativeElement.setSelectionRange(
            selectionEnd,
            selectionEnd
          );
        }
      } else if (operation === 'delete-back-single') {
        const prevChar = inputValue.charAt(selectionEnd - 1);
        const nextChar = inputValue.charAt(selectionEnd);
        const diff = currentLength - newLength;
        const isGroupChar = this._group.test(nextChar);

        if (isGroupChar && diff === 1) {
          selectionEnd += 1;
        } else if (!isGroupChar && this.isNumeralChar(prevChar)) {
          selectionEnd += -1 * diff + 1;
        }

        this._group.lastIndex = 0;
        this.input.nativeElement.setSelectionRange(selectionEnd, selectionEnd);
      } else if (inputValue === '-' && operation === 'insert') {
        this.input.nativeElement.setSelectionRange(0, 0);
        const index = this.initCursor();
        // tslint:disable-next-line: no-shadowed-variable
        const selectionEnd = index + insertedValueStr.length + 1;
        this.input.nativeElement.setSelectionRange(selectionEnd, selectionEnd);
      } else {
        selectionEnd = selectionEnd + (newLength - currentLength);
        if (selectionEnd < 0) {
          selectionEnd = 0;
        }
        this.input.nativeElement.setSelectionRange(selectionEnd, selectionEnd);
      }
    }

    this.input.nativeElement.setAttribute('aria-valuenow', value);
  }

  concatValues(val1: any, val2: any): any {
    if (val1 && val2) {
      const decimalCharIndex = val2.search(this._decimal);
      this._decimal.lastIndex = 0;

      return decimalCharIndex !== -1
        ? val1.split(this._decimal)[0] + val2.slice(decimalCharIndex)
        : val1;
    }

    return val1;
  }

  getDecimalLength(value: any): any {
    if (value) {
      const valueSplit = value.split(this._decimal);

      if (valueSplit.length === 2) {
        return valueSplit[1]
          .replace(this._suffix, '')
          .trim()
          .replace(/\s/g, '')
          .replace(this._currency, '').length;
      }
    }

    return 0;
  }

  onInputFocus(event: any): any {
    this.focused = true;
    this.onFocus.emit(event);
  }

  onInputBlur(event: any): any {
    this.focused = false;

    const newValue = this.validateValue(
      this.parseValue(this.input.nativeElement.value)
    );
    this.input.nativeElement.value = this.formatValue(newValue);
    this.input.nativeElement.setAttribute('aria-valuenow', newValue);
    this.updateModel(event, newValue);

    this.onBlur.emit(event);
  }

  formattedValue(): any {
    const val = !this.value && !this.allowEmpty ? 0 : this.value;
    return this.formatValue(val);
  }

  updateModel(event: any, value: any): any {
    if (this.value !== value) {
      this.value = value;
      this.onModelChange(value);
    }

    this.onModelTouched();
  }

  writeValue(value: any): void {
    this.value = value;
    this.cd.markForCheck();
  }

  // tslint:disable-next-line: ban-types
  registerOnChange(fn: Function): void {
    this.onModelChange = fn;
  }

  // tslint:disable-next-line: ban-types
  registerOnTouched(fn: Function): void {
    this.onModelTouched = fn;
  }

  setDisabledState(val: boolean): void {
    this.disabled = val;
    this.cd.markForCheck();
  }

  clearTimer(): void {
    if (this.timer) {
      clearInterval(this.timer);
    }
  }

  getFormatter(): any {
    return this.numberFormat;
  }
}
